<template>
  <div class="selection-date">
    <Loader v-if="loading" />

    <v-form
      v-else
      ref="form"
      class="content py-12"
      @submit.prevent="submit"
    >
      <div class="form-row">
        <DateField
          v-model="form.dateDebut"
          label="Date au plus tôt"
          :allowed-dates="isDateAllowed('DATE_DEBUT')"
          :rules="$fvm.form.dateDebut.$errors"
          close-on-select
        />
        <div v-if="!displayHoraires" />
        <v-select
          v-if="displayHoraires"
          v-model="form.horaireDebut"
          :items="periodesLivraison"
          label="Plage horaire"
          prepend-icon="mdi-clock-outline"
          :rules="$fvm.form.horaireDebut.$errors"
        />
      </div>
      <div class="form-row">
        <DateField
          v-model="form.dateFin"
          label="Date au plus tard"
          :allowed-dates="isDateAllowed('DATE_FIN')"
          :rules="$fvm.form.dateFin.$errors"
          close-on-select
        />
        <div
          v-if="!displayHoraires"
          class="btn-add"
          :class="{'mb-6':isMobile}"
          outlined
          color="grey"
          @click="displayHoraires = true"
        >
          + Ajouter des plages horaires
        </div>
        <v-select
          v-if="displayHoraires"
          v-model="form.horaireFin"
          :items="periodesLivraison"
          label="Plage horaire"
          prepend-icon="mdi-clock-outline"
          :rules="$fvm.form.horaireFin.$errors"
        />
      </div>
      <div
        v-if="displayHoraires"
        class="libelle-horaires"
      >
        <i>
          <LibelleDatesLivraison
            v-if="form.dateDebut && form.dateFin"
            :date-debut="form.dateDebut"
            :date-fin="form.dateFin"
            :horaire-debut="form.horaireDebut"
            :horaire-fin="form.horaireFin"
          />
        </i>
      </div>
      <div
        v-if="alerteLieux"
        class="form-row warning--text"
      >
        Attention, parmi les lignes de commandes sélectionnées, un ou plusieurs lieux de stockage sont prévus pour plusieurs lignes de commande aux mêmes dates de livraison.
      </div>

      <div class="form-group">
        <v-btn
          outlined
          color="grey"
          :block="isMobile"
          @click="cancel()"
        >
          Annuler
        </v-btn>

        <v-btn
          :block="isMobile"
          @click="submit()"
        >
          Ajouter au panier
        </v-btn>
      </div>
    </v-form>
    <div />
  </div>
</template>

<script>
import { cloneDeep, find } from 'lodash';

import { mapActions, mapGetters } from 'vuex';
import { format } from 'date-fns';
import { required, and, withMessage, custom, revalidate } from 'form-validation-manager';
import DatesLivraisonMixin from '../utils/dates-livraisons.mixin';
import ValidationsLieuxMixin from '../utils/validation-lieux.mixin';
import LibelleDatesLivraison from '../../../../components/libelle-dates-livraison';
import ArticlesApi from '@/api/articles';

function getOrDefault (fn, defaultvalue) {
  let value;
  try {
    value = fn();
  } catch (e) {}
  return value === undefined ? defaultvalue : value;
}

export default {
  name: 'AjoutRapideAuPanier',
  components: {
    LibelleDatesLivraison
  },
  mixins: [
    DatesLivraisonMixin,
    ValidationsLieuxMixin
  ],
  props: {
    lignes: {
      type: Array,
      default: null
    }
  },

  validations: {
    form: {
      dateDebut: and(
        withMessage(required(), 'Veuillez renseigner la période de livraison voulue'),
        custom(function (value) {
          return this.dateValidator('DATE_DEBUT', value);
        }),
        revalidate('form.dateFin'),
        revalidate('form.horaireFin')
      ),
      dateFin: and(
        withMessage(required(), 'Veuillez renseigner la période de livraison voulue'),
        custom(function (value) {
          return this.dateValidator('DATE_FIN', value);
        }),
        revalidate('form.horaireFin')
      ),
      horaireDebut: and(
        required(),
        custom(function (value) {
          if (this.validationHoraireDebut(value) === false) {
            return 'L\'horaire de début de livraison doit etre identique pour tous les aliments dont la date de début de livraison est la même';
          }
        }),
        revalidate('form.horaireFin')),
      horaireFin: and(
        required(),
        withMessage(custom(function (value) {
          // eslint-disable-next-line eqeqeq
          if (this.form.dateDebut == this.form.dateFin) {
            const horaires = this.form.horaireDebut + '-' + this.form.horaireFin;
            if (~['JS-JM', 'JS-JA', 'JS-JJ', 'JS-J1', 'JA-JM', 'JA-J1', 'JM-J1', 'JJ-J1'].indexOf(horaires)) {
              return true;
            }
          }
          return false;
        }), 'Les horaires ne sont pas valides pour une livraison sur un seul jour')
      )

    }
  },

  data () {
    return {
      lignes_: [],
      loading: true,
      displayHoraires: true,
      contraintesLivraison: {},
      supplementations: [],
      mineraux: [],
      form: {
        dateDebut: null,
        dateFin: null,
        horaireDebut: null,
        horaireFin: null
      }
    };
  },

  computed: {
    ...mapGetters({
      ref: 'referentiel/referentiel',
      paramsCommande: 'referentiel/paramsCommande',
      batiments: 'exploitation/batiments',
      numTiers: 'user/numTiers',
      datesLivraisonDefaut: 'cart/datesLivraisonDefaut',
      periodesLivraisonDefaut: 'cart/periodesLivraisonDefaut',
      panier: 'cart/cart'
    }),

    periodesLivraison () {
      return this.ref.periodesLivraison.map(item => ({ value: item.code, text: item.libelle }));
    },

    nbJoursEcartMinDatesLivraison () {
      let nbJoursEcartMin = 0;
      for (const ligne of this.lignes_) {
        if (ligne.codeFamilleArticle === '461' || ligne.codeModeCamion === 'VISP' || ligne.codeModeCamion === 'PNEUP') {
          nbJoursEcartMin = 1;
        }
      }

      return nbJoursEcartMin;
    },
    alerteLieux () {
      const err = this.$lieuxValidation.validationListLivraison(this.lignes_.map(ligne => ({
        datePrevueLivraison: this.form.dateDebut,
        finDatePrevueLivraison: this.form.dateFin,
        codeTypeSilo1: ligne.codeTypeSilo1,
        codeTypeSilo2: ligne.codeTypeSilo2,
        codeTypeSilo3: ligne.codeTypeSilo3
      })
      ), true);

      return err.alert || err.error;
    }
  },

  watch: {
    'form.dateDebut' (date) {
      if (!date) {
        return;
      }
      if (!this.form.dateFin) {
        const dateFin = this.$dateValidation.calculDateDefaut('DATE_FIN', date, null, 1, this.contraintesLivraison.delaisMinJoursOuvres, this.contraintesLivraison);
        this.form.dateFin = format(dateFin, 'yyyy-MM-dd');
      }

      this.dateCmdAnticipee(date);
    },
    'form.dateFin' (date) {
      if (!date) {
        return;
      }
      if (!this.form.dateDebut) {
        const dateDebut = this.$dateValidation.calculDateDefaut('DATE_DEBUT', null, date, 1, this.contraintesLivraison.delaisMinJoursOuvres, this.contraintesLivraison);
        this.form.dateDebut = format(dateDebut, 'yyyy-MM-dd');
      }

      this.ctrlIntervalleDateCmd(this.form.dateDebut, date);
    }
  },

  async created () {
    await Promise.all([
      this.loadParamsCommande({ numTiers: this.numTiers, numArticle: null }),
      this.$dateValidationLoad(),
      this.$lieuxValidationLoad(),
      this.getContraintesLivraison()
    ]);
    this.loading = false;

    this.lignes_ = cloneDeep(this.lignes);

    for (const ligne of this.lignes_) {
      if (!this.isBatimentValide(ligne.numBatiment, this.batiments)) {
        delete ligne.numBatiment;
      }

      [ligne.codeTypeSilo1, ligne.codeTypeSilo2, ligne.codeTypeSilo3] = [ligne.codeTypeSilo1, ligne.codeTypeSilo2, ligne.codeTypeSilo3]
        .map(lieu => this.isLieuValide(getOrDefault(() => lieu, null), ligne.numBatiment, this.batiments) ? lieu : null)
        .filter(lieu => !!lieu);
    }

    this.initform();
  },

  methods: {
    ...mapActions({
      addLineToCart: 'cart/addLine',
      loadParamsCommande: 'referentiel/loadParamsCommande',
      loadBatiments: 'exploitation/loadBatiments'
    }),
    initform () {
      this.displayHoraires = true;

      // on garde les periodes deja selectionnees
      this.form.horaireDebut = getOrDefault(() => this.periodesLivraisonDefaut.horaireDebut, 'ID');
      this.form.horaireFin = getOrDefault(() => this.periodesLivraisonDefaut.horaireFin, 'ID');

      if (this.datesLivraisonDefaut) {
        this.form.dateDebut = this.datesLivraisonDefaut.dateDebut;
        if (this.$dateValidation.validationDate('DATE_FIN', this.datesLivraisonDefaut.dateDebut, this.datesLivraisonDefaut.datefin, this.nbJoursEcartMinDatesLivraison, this.contraintesLivraison.delaisMinJoursOuvres, this.contraintesLivraison) === null) {
          this.form.dateFin = this.datesLivraisonDefaut.dateFin;
        } else {
          this.form.dateFin = format(this.$dateValidation.calculDateDefaut('DATE_FIN', this.form.dateDebut, null, 1, this.contraintesLivraison.delaisMinJoursOuvres, this.contraintesLivraison), 'yyyy-MM-dd');
        }
      }
    },
    isBatimentValide (numBatiment, batiments) {
      return !!find(batiments, { numBatiment });
    },
    isLieuValide (codeTypeSilo, numBatiment, batiments) {
      const batiment = find(batiments, { numBatiment });
      const typesSilo = batiment ? batiment.typesSilo : this.paramsCommande.typesSiloDefault;
      return !!find(typesSilo, { code: codeTypeSilo });
    },
    async getSuppMinerauxByArticle (codeTypeArticle, numArticle) {
      return Promise.all([
        ArticlesApi.getSupplementations(codeTypeArticle, numArticle, this.numTiers),
        ArticlesApi.getMineraux(codeTypeArticle, numArticle, this.numTiers)
      ]).then(([supplementations, mineraux]) => {
        supplementations = supplementations.sort(s => s.libelleLong)
          .map(s => { s.groupe = s.libelleLong.replace(/\d+([.,]\d+)?/g, ''); return s; })
          .map(article => ({ codeTypeArticle: article.codeTypeArticle, numArticle: article.numArticle, libelleArticle: article.libelleLong }));
        mineraux = mineraux.map(article => ({ codeTypeArticle: article.codeTypeArticle, numArticle: article.numArticle, libelleArticle: article.libelle }));

        return { supplementations, mineraux };
      });
    },
    async submit () {
      this.$refs.form.validate();

      if (this.$fvm.$isValid) {
        this.loading = true;
        await this.loadBatiments(this.numTiers);

        let nbLignesAjoutees = 0;

        for (const ligne of this.lignes_) {
          if (ligne.artCommandable !== true) {
            continue;
          }
          ligne.datePrevueLivraison = this.form.dateDebut;
          ligne.finDatePrevueLivraison = this.form.dateFin;
          ligne.matinApmidi = this.form.horaireDebut;
          ligne.finMatinApmidi = this.form.horaireFin;

          let supplementations = null;
          let mineraux = null;
          await this.getSuppMinerauxByArticle(ligne.codeTypeArticle, ligne.numArticle).then(response => {
            supplementations = response.supplementations;
            mineraux = response.mineraux;
          });
          ligne.supplementations = (cloneDeep(ligne.supplementations) || []).filter(s => find(supplementations, { numArticle: s.numArticle }));
          ligne.mineraux = (cloneDeep(ligne.mineraux) || []).filter(m => find(mineraux, { numArticle: m.numArticle }));

          ligne.numAdresse = ligne.numAdr || ligne.numAdresse || ligne.numeroAdresse || ligne.numExploitation || ligne.numeroExploitation;

          ligne.codeModeCamion = ligne.codeModeCamion || 'VIS';

          nbLignesAjoutees++;
          this.addLineToCart(ligne);
        }

        this.loading = false;

        const text = [];
        let actions = {
          help: { text: ' ', icon: 'mdi-information', color: 'grey', closable: false, flat: true, handle: () => this.$helpers.helpDialog('DUPLICATION_DERNIERE_COMMANDE') },
          continuer: { text: 'Continuer mes achats', flat: false },
          panier: { text: 'Voir mon panier', color: 'primary', flat: false }
        };
        if (nbLignesAjoutees > 0) {
          text.push('<i aria-hidden="true" style="font-size:42px;" class="v-icon notranslate mdi mdi-check-circle-outlignes theme--light"></i>');
          text.push(nbLignesAjoutees === 1 ? '1 article ajouté à votre panier' : nbLignesAjoutees + ' articles ajoutés à votre panier');

          const lignesNonAjoutés = this.lignes_.length - nbLignesAjoutees;
          if (lignesNonAjoutés > 0) {
            text.push('');
            text.push((lignesNonAjoutés === 1 ? '1 article ne peut être commandé' : lignesNonAjoutés + ' articles ne peuevent être commandés') + ' via cette application');
          }
        } else {
          text.push('<i aria-hidden="true" style="font-size:42px;" class="v-icon notranslate mdi mdi-alert-circle-outlignes theme--light"></i>');
          text.push('Aucun article de votre précédente commande ne peut être commandé via cette application');

          delete actions.panier;
        }

        this.$emit('cancel');
        setTimeout(() => {
          this.$dialog.confirm({ text: text.join('<br/>'), actions, showClose: false, width: '560px' }).then(
            result => {
              if (result === 'panier') {
                this.$router.push({ name: 'main.catalog.cart' });
              }
            }
          );
        });
      }
    },

    dateValidator (key, date) {
      const dateDebut = key === 'DATE_DEBUT' ? date : this.form.dateDebut;
      const dateFin = key === 'DATE_FIN' ? date : this.form.dateFin;

      return this.$dateValidation.validationDate(key, dateDebut, dateFin, this.nbJoursEcartMinDatesLivraison, this.contraintesLivraison.delaisMinJoursOuvres, this.contraintesLivraison);
    },

    validationHoraireDebut (code) {
      if (code === 'ID') {
        return true;
      }
      for (const ligne of this.panier) {
        if (ligne.datePrevueLivraison === this.form.dateDebut && ligne.matinApmidi !== 'ID' && code !== ligne.matinApmidi) {
          return false;
        }
      }

      return true;
    },

    isDateAllowed (key) {
      return (date) => {
        return this.dateValidator(key, date) == null;
      };
    },

    dateCmdAnticipee (date) {
      if (this.$dateValidation.validationDelais(date)) {
        this.$dialog
          .confirm({
            title: 'Période anticipée',
            text: 'La date début de période de livraison est anticipée. Voulez-vous continuer ? ',
            actions: { false: { text: 'Annuler', flat: false }, true: { text: 'Continuer', flat: false, outlined: true } }
          })
          .then(confirm => {
            if (!confirm) {
              this.form.dateDebut = null;
            }
          });
      }
    },

    ctrlIntervalleDateCmd (dateDebut, dateFin) {
      if (this.$dateValidation.validationIntervalle(dateDebut, dateFin)) {
        this.$dialog
          .confirm({
            title: 'Intervalle de date',
            text: 'L\' intervalle entre les deux dates de livraison est au-delà de 5 jours. Voulez-vous continuer ?  ',
            actions: { true: { text: 'Continuer', flat: false, outlined: true }, false: { text: 'Annuler', flat: false } }
          })
          .then(confirm => {
            if (!confirm) {
              this.form.dateFin = null;
            }
          });
      }
    },

    cancel () {
      this.$emit('cancel');
    },
    async getContraintesLivraison () {
      const promises = [];

      // Default
      const contraintesLivraison = { delaisMinJoursOuvres: 0, lundi: true, mardi: true, mercredi: true, jeudi: true, vendredi: true, samedi: true, dimanche: true };

      // call contraiontes pour tous les aliments
      for (const ligne of this.lignes) {
        const numAdr = ligne.numAdr || ligne.numAdresse || ligne.numeroAdresse || ligne.numExploitation || ligne.numeroExploitation;
        promises.push(ArticlesApi.getContraintesLivraison(ligne.codeTypeArticle, ligne.numArticle, this.numTiers, numAdr));
      }

      // agreagtion
      return Promise.all(promises).then(listContraintes => {
        for (const contraintes of listContraintes) {
          contraintesLivraison.delaisMinJoursOuvres = Math.max(contraintesLivraison.delaisMinJoursOuvres, contraintes.delaisMinJoursOuvres);
          contraintesLivraison.lundi &&= contraintes.lundi;
          contraintesLivraison.mardi &&= contraintes.mardi;
          contraintesLivraison.mercredi &&= contraintes.mercredi;
          contraintesLivraison.jeudi &&= contraintes.jeudi;
          contraintesLivraison.vendredi &&= contraintes.vendredi;
          contraintesLivraison.samedi &&= contraintes.samedi;
          contraintesLivraison.dimanche &&= contraintes.dimanche;

          if (contraintesLivraison.lundi && contraintesLivraison.mardi && contraintesLivraison.mercredi && contraintesLivraison.jeudi && contraintesLivraison.vendredi && contraintesLivraison.samedi && contraintesLivraison.dimanche === false) {
            this.$dialog
              .confirm({
                title: 'Attention',
                text: 'Il n\'y a aucun jour de livraison possible commun aux aliments sélectionnés.',
                actions: { true: { text: 'Fermer', flat: false, outlined: true } }
              }).then(() => { this.cancel(); });
          }
        }

        this.contraintesLivraison = contraintesLivraison;
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.selection-date {
  display: flex;
  flex-direction: row;
  align-items: center;
}

.v-form {
  background-color: #fff;
  padding: 3em;
  width: 100%;

  .form-row {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: center;

    & > * {
      max-width: 25em;
      width: 25em;
      margin-right: 2em;
    }
  }

  .form-group {
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: row;
    flex-wrap: wrap-reverse;
  }
}

@media #{map-get($display-breakpoints, 'sm-and-up')} {
  .v-form {
    .v-btn {
      margin: 0 1.5em;
    }
  }
}

@media #{map-get($display-breakpoints, 'xs-only')} {
  .v-form {
    padding: 1em;

    .v-btn {
      margin-top: 1em;
    }

    .form-row > * {
      max-width: 100%;
      width: 100%;
      margin: 0;
    }
  }
}

.btn-add {
  // font-size: 0.9em;
  color: grey;
  cursor: pointer;
  padding: 0.3em 1em 0.3em 0;
}

.libelle-horaires {
  margin: 0 0 2em 1em;
}
</style>
